import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import companyProgressService, {
  subscribeToJourneyChanges,
} from '../services/companyProgressService';

/**
 * Should this payload be re-read on a timer, and how often.
 *
 * EXPORTED so it can be asserted without fake timers: the rule is a statement
 * about the payload, not about the clock.
 *
 * Polling is narrow on purpose. The only moment the board can change without
 * this tab doing anything is while state 2 sits on a milestone whose next move
 * belongs to somebody else (a verification review, a provider confirmation).
 * Everywhere else the change signal and window focus already cover it, and a
 * standing minute timer on every open dashboard would be a request per minute
 * per recruiter for an answer that cannot have moved.
 */
export const progressRefetchInterval = (data) => {
  if (!data || data.state !== 2) return false;
  const focus = (data.activation?.milestones || []).find(
    (m) => m.id === data.activation?.focusMilestoneId,
  );
  return focus?.status === 'awaiting' ? 60000 : false;
};

/**
 * This account's aggregated progress payload.
 *
 * `known` is a third state, not a synonym for "has data". A failed read
 * reports `known: false` with a null payload rather than asserting an empty
 * board, and every caller branches on it before reading anything inside
 * `progress`. The two answers route differently: nav disclosure shows
 * everything on unknown, the billing module hides on it.
 *
 * `groupId` only keys the cache. It is optional because the dashboard reads
 * the payload before it necessarily knows the group, and a null key is still a
 * stable key for that session.
 */
const useCompanyProgress = (token, groupId = null) => {
  const queryClient = useQueryClient();

  const { data, isSuccess, isPending, refetch } = useQuery({
    queryKey: ['company', 'progress', groupId],
    queryFn: companyProgressService.getProgress,
    enabled: !!token,
    staleTime: 15000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => progressRefetchInterval(query?.state?.data),
  });

  useEffect(() => {
    // The mutation triggers. A publish, an import, a feed create, a swipe and a
    // recorded conversation all announce themselves through the same signal,
    // and a board that did not hear them would sit on a stale milestone until
    // the next navigation.
    const unsubscribe = subscribeToJourneyChanges(() => {
      queryClient.invalidateQueries({ queryKey: ['company', 'progress'] });
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [queryClient]);

  return {
    progress: data ?? null,
    known: isSuccess && !!data,
    loading: isPending && !!token,
    refetch,
  };
};

export default useCompanyProgress;
